/**
 * The theme switch in the masthead: light / system / dark as one small segmented
 * control, icon-only.
 *
 * Three visible states rather than a single cycling button: with three modes a cycle
 * has an order to learn, and "which one am I on" should be answerable at a glance.
 * The icons carry no text, so each radio names itself for assistive tech and the
 * group is labelled "Theme".
 */
import { usePreferences, THEMES, type Theme } from '@/ui/preferences'

const NAMES: Record<Theme, string> = { light: 'Light', system: 'System', dark: 'Dark' }

function Icon({ theme }: { theme: Theme }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  switch (theme) {
    case 'light':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="3" />
          <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1" />
        </svg>
      )
    case 'dark':
      return (
        <svg {...common}>
          <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7z" />
        </svg>
      )
    default:
      return (
        <svg {...common}>
          <rect x="1.75" y="3" width="12.5" height="8" rx="1.5" />
          <path d="M5.5 13.5h5M8 11v2.5" />
        </svg>
      )
  }
}

export function ThemeControl() {
  const { theme, setTheme } = usePreferences()
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      style={{
        display: 'inline-flex',
        border: '1px solid var(--rule-2)',
        borderRadius: 7,
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      {THEMES.map((o, i) => {
        const on = o.value === theme
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={NAMES[o.value]}
            title={`Theme: ${NAMES[o.value]}`}
            onClick={() => setTheme(o.value)}
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 30,
              height: 26,
              border: 0,
              borderLeft: i === 0 ? 0 : '1px solid var(--rule-2)',
              padding: 0,
              cursor: 'pointer',
              background: on ? 'var(--accent-soft)' : 'transparent',
              color: on ? 'var(--accent-ink)' : 'var(--ink-3)',
            }}
          >
            <Icon theme={o.value} />
          </button>
        )
      })}
    </div>
  )
}
